import { createFileRoute } from "@tanstack/react-router";
import { Box } from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle, SettingsBackBar, SettingsBackBarClearance } from "#app/components/page-header";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { useApiKeys, useApiKeyMutations } from "#app/hooks/settings/use-api-key-mutations";
import { ApiKeyFormView } from "#app/components/settings/api-key-form-view";
import type { Permissions } from "#app/components/settings/permission-badge";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

function ApiKeyEditPage() {
  const { apiKeyId } = Route.useParams();
  const { replace } = useAppNavigate();
  const { apiKeys, isLoading } = useApiKeys();
  const { updateApiKey } = useApiKeyMutations();

  const apiKey = apiKeys.find((k: any) => k.apiKeyId === apiKeyId);

  if (isLoading && !apiKey) {
    return (
      <Box direction="column" fill="flex" position="relative">
        {/* Real chrome: the back bar and the static header render for real;
          * the Settings card (name row + permission matrix) is approximated
          * at 4 rows. */}
        <SettingsBackBar label="API" onClick={() => replace(`/settings/api/keys/${apiKeyId}`)} />
        <Box fill="flex" overflow="auto">
          <SettingsBackBarClearance />
          <PageHeader centered pinnedBack>
            <PageHeaderTitle>Edit API key</PageHeaderTitle>
            <PageHeaderSubtitle>
              Actions made with this key are attributed to you.
            </PageHeaderSubtitle>
          </PageHeader>
          <SettingsPageSkeleton groups={[{ rows: 4 }]} />
        </Box>
      </Box>
    );
  }

  if (!apiKey) {
    replace("/settings/api");
    return null;
  }

  async function handleSubmit(data: { name: string; permissions: Permissions }) {
    // The form seeds from a cache object, which Apollo stamps with
    // `__typename`; the input type has no such field.
    const { __typename: _typename, ...cleanPerms } =
      data.permissions as Permissions & { __typename?: string };

    await updateApiKey(apiKeyId, {
      name: data.name,
      permissions: cleanPerms,
    });
    replace(`/settings/api/keys/${apiKeyId}`);
  }

  return (
    <ApiKeyFormView
      apiKey={{
        apiKeyId: apiKey.apiKeyId,
        name: apiKey.name,
        permissions: apiKey.permissions,
      }}
      onCancel={() => replace(`/settings/api/keys/${apiKeyId}`)}
      onSubmit={handleSubmit}
    />
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/api/keys/$apiKeyId/edit",
)({ component: withPermission("workspace.integrations", ApiKeyEditPage) });
